/*
 * Ryan O'Dowd
 * 2023-10-31
 * © Copyright 2023 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Image,
  Share,
  View,
} from 'react-native';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  fetchWebStatsMsSpent,
  fetchWebStatsNumVersesReviewed,
  upsertYearInReview,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import DownloadLinks from '@website/components/DownloadLinks';
import EmptyScreen from '@components/common/EmptyScreen';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import ScreenWrapper from '@containers/ScreenWrapper';
import Skeleton from '@components/common/Skeleton';
import Text from '@components/common/Text';
import {
  format as dateFnsFormat,
} from 'date-fns';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const YearInReview = (props: YearInReviewProps) => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);

  const statsRedux = hooks.useTypedSelector((state) => state.yearInReviewStats[locationState.year] || {}, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
  const isInFlight = hooks.useTypedSelector((state: any) => state.loadingStates.upsertYearInReview);

  const user = hooks.useTypedSelector((state) => state.oakUser);

  const msSpent = hooks.useTypedSelector((state) => state.webStatsMsSpent);
  const versesReviewed = hooks.useTypedSelector((state) => state.webStatsNumVersesReviewed);

  const [_stats, setStats] = useState(undefined);

  const year = locationState.year || _stats?.year;

  useEffect(() => {
    dispatch(fetchWebStatsMsSpent()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    dispatch(fetchWebStatsNumVersesReviewed()); // @TODO: this shouldn't be a live stat...maybe update every month or so, but i don't want people calling this api on every page load
    if (year) {
      dispatch(upsertYearInReview(year));
    }
  }, [dispatch, year]);

  useEffect(() => {
    if (!props.isPublic) {
      setStats(statsRedux);
    }
  }, [props.isPublic, statsRedux]);

  // @TODO: const lessonsPerDay = _stats.num_lessons / _stats.days_with_lessons;

  useEffect(() => {
    if (props.isPublic) {
      fetch(`${globals.apiUrl}/year_in_review/${locationState.token}`).then((response) => {
        if (response.status !== 200) {
          throw new Error('server error'); // @TODO: cleaner
        }
        return response.json();
      }).then((response) => {
        setStats(response.stats);
      }).catch((e) => {
        fb.recordError(fb.crashlytics, e);
        setStats(null);
      });
    }
  }, [props.isPublic, locationState.token]);

  if (_stats === undefined) {
    return ( // @TODO: better empty text/loading animation
      <EmptyScreen.Wrapper>
        <EmptyScreen.Text>
          Loading...
        </EmptyScreen.Text>
      </EmptyScreen.Wrapper>
    );
  }

  if (_stats === null) {
    return (
      <EmptyScreen.Wrapper>
        <EmptyScreen.Text>
          No year-end review found.  Please check the URL and try again.
        </EmptyScreen.Text>
      </EmptyScreen.Wrapper>
    );
  }

  const _renderSkeletonText = () => { // @TODO: abstract to its own component (param is number of lines to render)
    return (
      <View>
        <Skeleton style={styles.skeletonLine1} />
        <Skeleton style={styles.skeletonLine2} />
        <Skeleton style={styles.skeletonLine3} />
      </View>
    );
  };

  const backIcon = <Header.BackIcon />;

  return ( // @TODO: wrap in skeleton
    <ScreenWrapper>
      <Header
        leftItems={props.isPublic ? [] : [backIcon]}
        title={props.isPublic ? `${_stats.user_name}'s ${_stats.year} Wrinkly Summary` : `${year} year in review`}
      />
      <View style={styles.container}>
        <FullScreenScrollView
          contentContainerStyle={styles.mainListContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.section}>
            <View style={styles.textWrapper}>
              {isInFlight
                ? _renderSkeletonText()
                : (
                  i18n('yearInReviewLessons',
                    <Text style={styles.statText}>{_stats.num_lessons?.toLocaleString()} {_stats.num_lessons === 1 ? i18n('lesson') : i18n('lessons')}</Text>,
                    <Text style={styles.statText}>{_stats.days_with_lessons?.toLocaleString()} {_stats.days_with_lessons === 1 ? i18n('day') : i18n('days')}</Text>,
                    styles.statsWrapper,
                    // @TODO: {Math.floor(lessonsPerDay) > 1 ? `  (That's ${Math.floor(lessonsPerDay) !== Math.ceil(lessonsPerDay) && 'more than '}` : null}<Text style={styles.statText}>{Math.floor(lessonsPerDay)}</Text> lessons per day!)
                  )
                )
              }
            </View>
            <Image
              style={[styles.graphic, styles.elephantGraphic]}
              source={require('@assets/img/characters/elephant-success-shadow.png')}
            />
          </View>

          <View style={styles.section}>
            <View style={styles.textWrapper}>
              {isInFlight
                ? _renderSkeletonText()
                : (
                  i18n('yearInReviewTime',
                    <Text style={styles.statText}>{utilities.humanReadableTime(_stats.ms_spent)}</Text>,
                    <Text style={styles.statText}>{utilities.humanReadableNumber(_stats.num_verses)} {_stats.num_verses === 1 ? i18n('verseLowercase') : i18n('versesLowercase')}</Text>,
                    <Text style={styles.statText}>{utilities.humanReadableNumber(_stats.peanuts_earned)} {_stats.peanuts_earned === 1 ? 'peanut' : i18n('peanutsLowercase')}</Text>,// @TODO: i18n peanut
                    styles.statsWrapper,
                    // @TODO: {Math.floor(lessonsPerDay) > 1 ? `  (That's ${Math.floor(lessonsPerDay) !== Math.ceil(lessonsPerDay) && 'more than '}` : null}<Text style={styles.statText}>{Math.floor(lessonsPerDay)}</Text> lessons per day!)
                  )
                  /* @TODO: looks like you preferred {difficulty} difficulty, and your favorite game type is {gametype}
                    <p>TODO: pie chart for lesson difficulty</p>
                    <p>TODO: pie chart for types of games</p>
                  */
                )
              }
            </View>
          </View>

          <View style={styles.section}>
            <Image
              style={[styles.graphic, styles.brainGraphic]}
              source={require('@assets/img/characters/brain-success-shadow-right.png')}
            />
            <View style={styles.textWrapper}>
              {isInFlight
                ? _renderSkeletonText()
                : (
                  i18n('yearInReviewGoalsAndWrinkles',
                    <Text style={styles.statText}>{utilities.humanReadableNumber(_stats.num_goals_worked_on)} {_stats.num_goals_worked_on === 1 ? i18n('goal') : i18n('goalsLowercase')}</Text>,
                    <Text style={styles.statText}>{_stats.num_wrinkles_earned?.toLocaleString()} {_stats.num_wrinkles_earned === 1 ? i18n('wrinkle') : i18n('wrinklesLowercase')}</Text>,
                    _stats.num_wrinkles_earned,
                    _stats.num_wrinkles_earned === 1 ? i18n('verseLowercase') : i18n('versesLowercase'),
                    (_stats.num_wrinkles_in_progress && new Date().getFullYear() === year) ? `${_stats.num_wrinkles_in_progress} ${_stats.num_wrinkles_in_progress === 1 ? i18n('wrinkle') : i18n('wrinklesLowercase')}` : null, // @TODO: i don't think this needs to be stored in the db (in the year_in_reviews table at least)
                    styles.statsWrapper,
                  )
                )
              }
              {/* @TODO: maybe two more paragraphs here */}
              {isInFlight
                ? _renderSkeletonText()
                : (
                  !!_stats.num_friends && (
                    i18n('yearInReviewMiscFriends',
                      <Text style={styles.statText}>{_stats.num_friends} {_stats.num_friends === 1 ? i18n('friend') : i18n('friendsLowercase')}</Text>, // @TODO: make this interactive, reword, and also have alternative text if _stats.num_friends === 0
                      props.isPublic,
                      styles.statsWrapper,
                    )
                  )
                )
              }
            </View>
            {/* @TODO:
            <Image
              style={styles.graphic}
              source={require('@assets/img/characters/monkeys.png')}// @TODO: update this
            />
            */}
          </View>

          {/* @TODO:
          <View style={styles.section}>
            <View style={styles.textWrapper}>
              {i18n('yearInReviewMiscDays',
                <Text style={styles.statText}>{_stats.days_with_lessons} {_stats.days_with_lessons === 1 ? i18n('day') : i18n('days')}</Text>,
                styles.statsWrapper,
              )}
            </View>
          </View>
          */}

          {/* @TODO: maybe one more paragraph */}

          {!props.isPublic &&
            <View style={styles.shareWrapper}>
              <Button
                mode='contained'
                theme={styles.colors.accent}
                onPress={() => {
                  const url = `${globals.webUrl}/my-year/${_stats.token}`;
                  fb.logEvent(fb.analytics, 'share_link', {location: 'year_in_review'});
                  Share.share({
                    message: i18n('yearInReviewShareMessage', year, url),
                    url,
                    title: i18n('shareOptionsTitle'),
                  }, {
                    // Android only:
                    dialogTitle: i18n('shareWrinkly'),
                  });
                }}
              >
                {i18n('share')}
              </Button>
            </View>
          }

          <View style={styles.section}>
            <View style={styles.textWrapper}>
              {isInFlight
                ? _renderSkeletonText()
                : (
                  <React.Fragment>
                    {i18n('yearInReviewLifetime',
                      dateFnsFormat(new Date(user.created_at), 'MMMM d, yyyy'),
                      <Text style={styles.statText}>{user.lifetime_peanuts?.toLocaleString()} {user.lifetime_peanuts === 1 ? 'peanut' : i18n('peanutsLowercase')}</Text>,// @TODO: i18n peanut
                      <Text style={styles.statText}>{user.lifetime_wrinkles?.toLocaleString()} {user.lifetime_wrinkles === 1 ? i18n('wrinkle') : i18n('wrinklesLowercase')}</Text>,
                      styles.statsWrapper,
                    )}
                    {i18n('yearInReviewGlobal',
                      <Text style={styles.statText}>{utilities.humanReadableTime(msSpent)}</Text>,
                      <Text style={styles.statText}>{utilities.humanReadableNumber(versesReviewed)} {i18n('versesLowercase')}</Text>,
                      styles.statsWrapper,
                    )}
                  </React.Fragment>
                )
              }
            </View>
          </View>

          {!!props.isPublic &&
            <View style={styles.downloadCtaWrapper}>
              <Text style={styles.sectionTitle}>Try Wrinkly for yourself!</Text>{/* @TODO: make this "share wrinkly" instead and have options to share year in review with social media and stuff */}{/* @TODO: i18n */}
              <DownloadLinks />
            </View>
          }
        </FullScreenScrollView>
      </View>
    </ScreenWrapper>
  );
};

class YearInReviewProps {
  isPublic?: boolean;
};

export default YearInReview;
